import accessMappings, {
  ProviderAccessMap,
} from '@vgtv/svp-access/lib/access_mappings';
import type { StreamProvider } from '@vgtv/api-client';
import { useEffect, useState } from 'preact/hooks';

import { subscribe, waitForInitialization } from '../../../config/paywall/spid';
import { useSchibstedAccount } from '../../../utils/SchibstedAccountProvider';

import { useProvider } from './useProvider';

const DEFAULT_TIMEOUT_IN_MILLISECONDS = 4000;

const getLoginAccessName = (provider: StreamProvider) => {
  const index = Object.values(accessMappings[provider])?.findIndex(
    ({ accessControl }) => accessControl === 'login'
  );
  return index >= 0 ? Object.keys(accessMappings[provider])[index] : undefined;
};

const hasAccessLevel = (
  accesses: ProviderAccessMap[StreamProvider][],
  accessLevel: ProviderAccessMap[StreamProvider] | 'login' | undefined,
  provider: StreamProvider,
  loggedIn: boolean
) => {
  if (!accessLevel) {
    return true;
  }
  if (loggedIn && accessLevel === 'login') {
    return true;
  }
  const loginAccessName = getLoginAccessName(provider);
  if (loggedIn && loginAccessName === accessLevel) {
    return true;
  }
  return accesses.includes(accessLevel);
};

export const usePaywallState = <T extends StreamProvider>(
  accessLevel?: ProviderAccessMap[T] | 'login'
) => {
  const { getUserId, getUserAccesses } = useSchibstedAccount();
  const { configProvider } = useProvider();
  const [accesses, setAccesses] = useState<ProviderAccessMap[T][]>([]);
  // Only show loading state for paywalled assets while identity is resolving.
  const [isLoading, setIsLoading] = useState(!!accessLevel);
  // Force re-render when the snapshot notifies us of new identity data.
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    return subscribe(() => forceUpdate((n) => n + 1));
  }, []);

  // Wait for the identity initialization window to close, then stop loading.
  useEffect(() => {
    if (!accessLevel) return;

    waitForInitialization(DEFAULT_TIMEOUT_IN_MILLISECONDS).then(() => {
      setIsLoading(false);
    });
  }, [accessLevel]);

  const loggedIn = !!getUserId();

  useEffect(() => {
    if (loggedIn && accessLevel) {
      getUserAccesses().then(setAccesses as any);
    }
  }, [loggedIn, getUserAccesses, accessLevel]);

  const isGranted = hasAccessLevel(
    accesses,
    accessLevel,
    configProvider,
    loggedIn
  );
  const canUpgrade = loggedIn && !isGranted;

  return {
    isLoggedIn: loggedIn,
    hasAccess: isGranted,
    canUpgrade,
    isLoading,
  };
};
